import { FC, ReactNode, useEffect, useState } from 'react';
import { useFeatureFlag } from 'src/hooks/useFeatureFlag/useFeatureFlag';
import { FeatureFlagKey } from 'src/ts/enums';
import { IProof } from 'src/ts/interfaces';
import { IListCheckCandidate, IListCheckEntry } from 'src/ts/interfaces/listCheck';
import { CandidateXrayState } from '../../hooks/useCandidateXray';
import { XrayAnalysisState } from '../../hooks/useXrayAnalysis';
import {
  candidateFieldsLine,
  fieldText,
  humanizeNotRunReason,
  isMatchRuling,
  isResolved,
  listCheckChip
} from '../../helpers/listCheckHelpers';
import {
  ListCheckAnswered,
  ListCheckCandidateSub,
  ListCheckDetailBody,
  ListCheckDetailFoot,
  ListCheckDetailHead,
  ListCheckEmptyState,
  ListCheckExternalLink,
  ListCheckFootSpacer,
  ListCheckLede,
  ListCheckLedeRow,
  ListCheckLedeValue,
  ListCheckMatchedCard,
  ListCheckMatchedLabel,
  ListCheckMatchedRef,
  ListCheckRowLink,
  ListChecksDetail,
  ListCheckSecondaryButton,
  ListCheckSelectButton,
  ListCheckTabButton,
  ListCheckTabStrip,
  ListCheckTitle,
  ListCheckTitleRow
} from '../../styles';
import BytecodeCompare from './BytecodeCompare';
import CandidateTile from './CandidateTile';
import ListCheckChip from './ListCheckChip';
import { listCheckCopy } from './listCheckCopy';
import NoneOfTheseModal from './NoneOfTheseModal';
import CandidateNotes from './CandidateNotes';
import ProviderMismatchModal from './ProviderMismatchModal';
import RegistryCard from './RegistryCard';
import StackNav from './StackNav';
import TextPropertiesCompare, { TextSide } from './TextPropertiesCompare';
import AdvancedFraudTrendsDetail from './advancedFraudTrends/AdvancedFraudTrendsDetail';

interface ListCheckDetailPaneProps {
  entry: IListCheckEntry;
  currentProof?: IProof;
  surface?: string;
  documentXray?: XrayAnalysisState;
  candidateXray?: CandidateXrayState;
  isSubmitting: boolean;
  canAnswer: boolean;
  onSelect: (rowId: string) => void;
  onProviderMismatch: (rowId: string, note?: string) => void;
  onNoneOfThese: (note?: string) => void;
  onFraudTrendAnswer: (edited: boolean, note?: string) => void;
  onUndo: () => void;
}

const candidateRef = (candidate: IListCheckCandidate): string | null => {
  const parts = [candidate.row_ref, candidateFieldsLine(candidate)].filter(Boolean);
  return parts.length ? parts.join(' · ') : null;
};

const lede = (entry: IListCheckEntry, simpleRegistry: boolean): ReactNode => {
  if (simpleRegistry && isResolved(entry) && entry.ruling !== 'no_match') {
    return (
      <>
        {listCheckCopy.youAnswered}{' '}
        <ListCheckLedeValue>
          {entry.ruling === 'match_mismatch' ? listCheckCopy.mismatch : listCheckCopy.match}
        </ListCheckLedeValue>
        .
      </>
    );
  }
  if (isResolved(entry)) {
    return entry.ruling === 'no_match' ? (
      <>
        {listCheckCopy.youAnswered} <ListCheckLedeValue>{listCheckCopy.noMatch}</ListCheckLedeValue>
        .
      </>
    ) : (
      <>
        {listCheckCopy.youPicked}{' '}
        <ListCheckLedeValue>
          {entry.candidate_pick?.label ?? entry.matched_row?.label ?? 'Match'}
        </ListCheckLedeValue>
        .
      </>
    );
  }
  if (entry.ruling === 'not_run') {
    return `The engine could not run this check (${humanizeNotRunReason(
      entry.ruling_reason
    )}), so it has no answer.`;
  }
  if (entry.ruling === 'no_match') return 'Nothing on this list came close to the document.';
  if (entry.ruling === 'needs_review' && entry.ruling_reason === 'provider_unknown') {
    return listCheckCopy.registryUnread;
  }
  return null;
};

const observedProvider = (entry: IListCheckEntry): string | null => entry.observed.payroll_provider;

const Observed: FC<{ entry: IListCheckEntry }> = ({ entry }) => {
  const provider = observedProvider(entry);
  return provider ? (
    <ListCheckMatchedRef data-testid="list-check-observed">
      Payroll provider on the document: {provider}
    </ListCheckMatchedRef>
  ) : null;
};

const RegisteredProviders: FC<{ entry: IListCheckEntry }> = ({ entry }) =>
  entry.ruling === 'match_mismatch' &&
  observedProvider(entry) &&
  entry.registered_providers.length ? (
    <ListCheckCandidateSub data-testid="list-check-registered-providers">
      Registered providers: {entry.registered_providers.join(', ')}
    </ListCheckCandidateSub>
  ) : null;

const registryLabel = (matchedRow: IListCheckCandidate): string | null => {
  const provider = matchedRow.fields?.payroll_provider;
  return provider
    ? `${matchedRow.label} — registered provider: ${fieldText(provider)}`
    : matchedRow.label;
};

const matchedCard = (entry: IListCheckEntry, matchedRow: IListCheckCandidate) => {
  const registry = isRegistry(entry);
  const ref = registry ? matchedRow.row_ref : candidateRef(matchedRow);
  const fields = registry ? null : candidateFieldsLine(matchedRow);
  return (
    <>
      <ListCheckMatchedCard data-testid="list-check-matched">
        <ListCheckMatchedLabel>
          {registry ? registryLabel(matchedRow) : matchedRow.label}
        </ListCheckMatchedLabel>
        {ref && (
          <ListCheckMatchedRef>
            {matchedRow.row_url && matchedRow.row_ref ? (
              <>
                <ListCheckRowLink
                  id="list-check-open-row"
                  href={matchedRow.row_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  title={listCheckCopy.openRow}
                >
                  {matchedRow.row_ref}
                </ListCheckRowLink>
                {fields && ` · ${fields}`}
              </>
            ) : (
              ref
            )}
          </ListCheckMatchedRef>
        )}
      </ListCheckMatchedCard>
      <CandidateNotes candidate={matchedRow} />
    </>
  );
};

const TEXT_COMPARE_CHECKS = ['allow_list', 'threat_templates'];

type CandidateTab = 'overview' | 'text' | 'bytecode';

const isEngineMatch = (entry: IListCheckEntry): boolean =>
  !isResolved(entry) && isMatchRuling(entry.ruling);

const isRegistry = (entry: IListCheckEntry): boolean =>
  entry.check_key === 'employer_payroll_registry';

// Registry matches keep the provider card instead of a document compare tile.
const stackRows = (entry: IListCheckEntry): IListCheckCandidate[] => {
  if (entry.ruling === 'needs_review' || isResolved(entry)) return entry.candidates;
  if (isEngineMatch(entry) && !isRegistry(entry) && entry.matched_row) return [entry.matched_row];
  return [];
};

const Fallback: FC<{ entry: IListCheckEntry }> = ({ entry }) => {
  if (isResolved(entry)) return entry.matched_row ? matchedCard(entry, entry.matched_row) : null;
  if (entry.ruling === 'not_run') {
    return (
      <ListCheckEmptyState>
        Nothing to compare. Open the list and check it by hand.
      </ListCheckEmptyState>
    );
  }
  if (isMatchRuling(entry.ruling)) {
    return entry.matched_row ? (
      matchedCard(entry, entry.matched_row)
    ) : (
      <ListCheckEmptyState>No matched row was recorded.</ListCheckEmptyState>
    );
  }
  if (entry.ruling === 'needs_review' && entry.ruling_reason === 'always_needs_review') {
    return (
      <ListCheckEmptyState>
        Nothing on this list came close to the document. Open the list and check it by hand.
      </ListCheckEmptyState>
    );
  }
  if (entry.ruling === 'needs_review') {
    return (
      <ListCheckEmptyState>
        No candidates cleared the bar. Open the list and check it by hand.
      </ListCheckEmptyState>
    );
  }
  return <ListCheckEmptyState>No candidate rows.</ListCheckEmptyState>;
};

const TICK = (
  <svg
    width="13"
    height="13"
    viewBox="0 0 12 12"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <path d="M2 6.4 4.6 9 10 3.2" />
  </svg>
);

const pickIndex = (entry: IListCheckEntry, rows: IListCheckCandidate[]): number =>
  Math.max(
    rows.findIndex(
      (row) => row.row_id !== null && row.row_id === entry.candidate_pick?.list_row_id
    ),
    0
  );

const ListCheckDetailPane: FC<ListCheckDetailPaneProps> = (props) => {
  const { entry } = props;
  return entry.is_fraud_trend ? (
    <AdvancedFraudTrendsDetail {...props} />
  ) : (
    <ListCheckDetail {...props} />
  );
};

const ListCheckDetail: FC<ListCheckDetailPaneProps> = (props) => {
  const {
    entry,
    currentProof,
    documentXray,
    candidateXray,
    isSubmitting,
    canAnswer,
    onSelect,
    onProviderMismatch,
    onNoneOfThese,
    onUndo
  } = props;
  const [confirmingNone, setConfirmingNone] = useState(false);
  const [confirmingMismatch, setConfirmingMismatch] = useState<string | null>(null);
  const requireNote = useFeatureFlag(FeatureFlagKey.ITL_3488_NO_MATCH_COMMENT);
  const simpleRegistry =
    isRegistry(entry) && (entry.ruling === 'needs_review' || isResolved(entry));
  const [answeredHere, setAnsweredHere] = useState(false);
  const rows = stackRows(entry);
  const [position, setPosition] = useState(() => pickIndex(entry, rows));
  const n = rows.length;
  const index = n ? ((position % n) + n) % n : 0;
  const active = rows[index];
  const chip = listCheckChip(entry);
  const ledeText = lede(entry, simpleRegistry);
  const resolved = isResolved(entry);
  const selectable = active ?? (isEngineMatch(entry) ? entry.matched_row : null);
  const answerable = entry.ruling === 'needs_review' || isEngineMatch(entry);
  const registryRow = simpleRegistry ? active ?? entry.matched_row : null;
  const showFoot = simpleRegistry
    ? !resolved && canAnswer
    : resolved || entry.ruling === 'needs_review' || !!selectable;
  const multipleMatches =
    entry.ruling === 'needs_review' && entry.ruling_reason === 'multiple_matches';
  const warnNone = multipleMatches || (isEngineMatch(entry) && !!selectable);
  const confirmRows = multipleMatches
    ? entry.candidates
    : warnNone && selectable
    ? [selectable]
    : [];
  const isPick =
    resolved && !!active?.row_id && active.row_id === entry.candidate_pick?.list_row_id;
  const showTabs = !simpleRegistry && !!active && TEXT_COMPARE_CHECKS.includes(entry.check_key);
  const [tab, setTab] = useState<CandidateTab>('overview');
  const showText = showTabs && tab === 'text';
  const showBytecode = showTabs && tab === 'bytecode';
  const candidateReady = !!active?.row_id && !!active?.example_document_url;
  const preloadIds = showTabs
    ? rows
        .filter((row) => row.row_id && row.example_document_url)
        .map((row) => row.row_id as string)
    : [];
  const shownRowId = showTabs && candidateReady ? active?.row_id ?? null : null;
  const preloadKey = JSON.stringify(preloadIds);
  const preload = candidateXray?.preload;
  useEffect(() => {
    const rowIds: string[] = JSON.parse(preloadKey);
    if (preload && rowIds.length) preload(entry.check_key, rowIds, shownRowId);
  }, [preload, entry.check_key, preloadKey, shownRowId]);
  useEffect(() => {
    if (resolved) {
      setConfirmingNone(false);
      setConfirmingMismatch(null);
    }
  }, [resolved]);
  const candidateResult =
    candidateXray && active?.row_id
      ? candidateXray.resultFor(entry.check_key, active.row_id)
      : null;
  const sides: { document: TextSide; candidate: TextSide } | null = active
    ? {
        document: {
          title: listCheckCopy.thisDocument,
          aside: currentProof?.short_id,
          status: documentXray?.status ?? 'idle',
          data: documentXray?.data ?? null,
          onRetry: documentXray?.retry
        },
        candidate: {
          title: listCheckCopy.candidate(index + 1),
          aside: active.row_ref,
          status: candidateReady ? candidateResult?.status ?? 'idle' : 'unavailable',
          data: candidateResult?.data ?? null,
          onRetry: () => {
            if (active.row_id) candidateXray?.retry(entry.check_key, active.row_id);
          }
        }
      }
    : null;

  const answerNoneOfThese = (note?: string) => {
    setAnsweredHere(true);
    onNoneOfThese(note);
  };

  const requestNoneOfThese = () => {
    if (requireNote || warnNone) {
      setConfirmingNone(true);
    } else {
      answerNoneOfThese();
    }
  };

  const confirmNoneOfThese = (note?: string) => {
    if (!requireNote) setConfirmingNone(false);
    answerNoneOfThese(note);
  };

  const select = (rowId: string) => {
    setAnsweredHere(true);
    onSelect(rowId);
  };

  const recordProviderMismatch = (rowId: string, note?: string) => {
    setAnsweredHere(true);
    onProviderMismatch(rowId, note);
  };

  const requestProviderMismatch = (rowId: string) => {
    if (requireNote) {
      setConfirmingMismatch(rowId);
    } else {
      recordProviderMismatch(rowId);
    }
  };

  const undo = () => {
    setAnsweredHere(false);
    onUndo();
  };

  return (
    <ListChecksDetail data-testid="list-check-detail-pane">
      <ListCheckDetailHead>
        <ListCheckTitleRow>
          <ListCheckTitle>{entry.display_name}</ListCheckTitle>
          {chip && <ListCheckChip chip={chip} />}
          {entry.list_url && (
            <ListCheckExternalLink
              id="list-check-open-list"
              href={entry.list_url}
              target="_blank"
              rel="noopener noreferrer"
            >
              Open the {entry.sheet_tab} tab
              <svg
                width="11"
                height="11"
                viewBox="0 0 12 12"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
                aria-hidden="true"
              >
                <path d="M4.5 1.5H1.5v9h9v-3" />
                <path d="M7.5 1.5h3v3" />
                <path d="M10.5 1.5 5.5 6.5" />
              </svg>
            </ListCheckExternalLink>
          )}
        </ListCheckTitleRow>
        {(ledeText || resolved) && (
          <ListCheckLedeRow>
            {ledeText && <ListCheckLede>{ledeText}</ListCheckLede>}
            {resolved && (
              <ListCheckSecondaryButton
                type="button"
                id="list-check-undo"
                data-testid="list-check-undo"
                disabled={isSubmitting}
                onClick={undo}
              >
                {listCheckCopy.undo}
              </ListCheckSecondaryButton>
            )}
          </ListCheckLedeRow>
        )}
      </ListCheckDetailHead>
      {showTabs && (
        <ListCheckTabStrip role="tablist">
          <ListCheckTabButton
            type="button"
            role="tab"
            id="list-check-tab-overview"
            data-testid="list-check-tab-overview"
            aria-selected={tab === 'overview'}
            $active={tab === 'overview'}
            onClick={() => setTab('overview')}
          >
            {listCheckCopy.overviewTab}
          </ListCheckTabButton>
          <ListCheckTabButton
            type="button"
            role="tab"
            id="list-check-tab-text-properties"
            data-testid="list-check-tab-text-properties"
            aria-selected={tab === 'text'}
            $active={tab === 'text'}
            onClick={() => setTab('text')}
          >
            {listCheckCopy.textPropertiesTab}
          </ListCheckTabButton>
          <ListCheckTabButton
            type="button"
            role="tab"
            id="list-check-tab-bytecode"
            data-testid="list-check-tab-bytecode"
            aria-selected={tab === 'bytecode'}
            $active={tab === 'bytecode'}
            onClick={() => setTab('bytecode')}
          >
            {listCheckCopy.bytecodeTab}
          </ListCheckTabButton>
        </ListCheckTabStrip>
      )}
      <ListCheckDetailBody data-testid="list-check-detail-body">
        {resolved && (
          <ListCheckAnswered data-testid="list-check-answered">
            {TICK}
            {answeredHere
              ? `${listCheckCopy.answeredBy} · ${listCheckCopy.justNow}`
              : listCheckCopy.answeredBy}
          </ListCheckAnswered>
        )}
        <Observed entry={entry} />
        <RegisteredProviders entry={entry} />
        {registryRow && (
          <RegistryCard
            checkKey={entry.check_key}
            row={registryRow}
            providers={entry.registered_providers}
            currentProof={currentProof}
          />
        )}
        {(showText || showBytecode) && active && sides && (
          <>
            {showText ? (
              <TextPropertiesCompare document={sides.document} candidate={sides.candidate} />
            ) : (
              <BytecodeCompare document={sides.document} candidate={sides.candidate} />
            )}
            <CandidateNotes candidate={active} />
          </>
        )}
        {!showText && !showBytecode && !simpleRegistry && active && (
          <CandidateTile
            entry={entry}
            candidate={active}
            position={index + 1}
            currentProof={currentProof}
            isPick={isPick}
          />
        )}
        {!registryRow && !active && <Fallback entry={entry} />}
      </ListCheckDetailBody>
      {showFoot && (
        <ListCheckDetailFoot data-testid="list-check-detail-foot">
          {canAnswer && answerable && (
            <ListCheckSecondaryButton
              type="button"
              id="list-check-none-of-these"
              data-testid="list-check-none-of-these"
              disabled={isSubmitting}
              onClick={requestNoneOfThese}
            >
              {listCheckCopy.noMatch}
            </ListCheckSecondaryButton>
          )}
          <ListCheckFootSpacer />
          {active && <StackNav count={n} index={index} onChange={setPosition} />}
          {simpleRegistry && active?.row_id && (
            <>
              <ListCheckSecondaryButton
                type="button"
                data-testid="list-check-mismatch"
                disabled={isSubmitting}
                onClick={() => active.row_id && requestProviderMismatch(active.row_id)}
              >
                {listCheckCopy.mismatch}
              </ListCheckSecondaryButton>
              <ListCheckSelectButton
                type="button"
                data-testid="list-check-match"
                disabled={isSubmitting}
                onClick={() => active.row_id && select(active.row_id)}
              >
                {listCheckCopy.match}
              </ListCheckSelectButton>
            </>
          )}
          {!simpleRegistry && selectable && canAnswer && (
            <ListCheckSelectButton
              type="button"
              id="list-check-select"
              data-testid="list-check-select"
              disabled={resolved || isSubmitting || !selectable.row_id}
              onClick={() => selectable.row_id && select(selectable.row_id)}
            >
              {isPick ? listCheckCopy.selected : listCheckCopy.select}
            </ListCheckSelectButton>
          )}
        </ListCheckDetailFoot>
      )}
      {confirmingNone && (
        <NoneOfTheseModal
          displayName={entry.display_name}
          candidates={confirmRows}
          isLoading={isSubmitting}
          requireNote={requireNote}
          onConfirm={confirmNoneOfThese}
          onCancel={() => setConfirmingNone(false)}
        />
      )}
      {confirmingMismatch && (
        <ProviderMismatchModal
          isLoading={isSubmitting}
          onConfirm={(note) => recordProviderMismatch(confirmingMismatch, note)}
          onCancel={() => setConfirmingMismatch(null)}
        />
      )}
    </ListChecksDetail>
  );
};

export default ListCheckDetailPane;
